import { useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Sparkles, Loader2 } from 'lucide-react';

interface PluginGuidedWelcomeProps {
  onStart: (idea: string, name?: string) => void;
  isProcessing: boolean;
  idea: string;
  onIdeaChange: (idea: string) => void;
  name: string;
  onNameChange: (name: string) => void;
}

export function PluginGuidedWelcome({
  onStart,
  isProcessing,
  idea,
  onIdeaChange,
  name,
  onNameChange,
}: PluginGuidedWelcomeProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  /** Capitalize the first letter of each word */
  const toTitleCase = (value: string) => value.replace(/\b\w/g, (char) => char.toUpperCase());

  const handleSubmit = () => {
    const trimmed = idea.trim();
    if (!trimmed || isProcessing) return;
    const finalName = name.trim() ? toTitleCase(name.trim()) : undefined;
    onStart(trimmed, finalName);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div className="flex-1 flex flex-col items-center justify-center p-6 gap-6 max-w-lg mx-auto">
      <div className="text-center space-y-2">
        <Sparkles className="h-8 w-8 mx-auto text-primary" />
        <h2 className="text-xl font-semibold">What Arche would you like to build?</h2>
        <p className="text-sm text-muted-foreground">
          Describe your idea and we'll guide you through the entire build process.
        </p>
      </div>

      <Textarea
        ref={textareaRef}
        value={idea}
        onChange={(e) => {
          onIdeaChange(e.target.value);
          autoResize();
        }}
        onKeyDown={handleKeyDown}
        placeholder="Put the description of the Arche you would like to build here. Please give as much detail as possible."
        className="min-h-[100px] resize-none overflow-hidden"
        disabled={isProcessing}
        autoFocus
      />

      <div className="w-full space-y-1">
        <label htmlFor="plugin-name" className="text-sm font-medium">
          Plugin name (optional)
        </label>
        <Input
          id="plugin-name"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="My Plugin"
          disabled={isProcessing}
        />
      </div>

      <Button
        onClick={handleSubmit}
        disabled={!idea.trim() || isProcessing}
        className="w-full"
        size="lg"
      >
        {isProcessing ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Sparkles className="mr-2 h-4 w-4" />
        )}
        Start Building
      </Button>
    </div>
  );
}
